"use client";

import { c as _c } from "react/compiler-runtime";
import { withLineQuantity } from "../cart-line-utils";
import { CatalogTileSkeleton, hasPromotion } from "../components/catalog-card-primitives";
import { shopPhotoTransitionType } from "../components/shop-photo-transition";
import { ShopSortSelect } from "../components/shop-sort-select";
import { StorefrontProductTileSkeleton } from "../components/storefront-product-tile";
import { GiftCardShopTile } from "../gift-cards/catalog";
import GiftCardConfiguratorDialog, { GiftCardConfiguratorSelection } from "../gift-cards/configurator-dialog";
import { MedicationTile } from "../medications/catalog";
import { MembershipTile } from "../memberships/catalog";
import MembershipConfiguratorDialog from "../memberships/configurator-dialog";
import { PackageTile } from "../packages/catalog";
import PackageDetailsDialog from "../packages/details-dialog";
import { packageLineFromItem } from "../packages/utils";
import { ProductTile } from "../products/catalog";
import { giftPromotionItems } from "../promotion-messaging";
import { ServiceTile } from "../services/catalog";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiTag } from "react-icons/fi";
import { useParams, usePathname, useRouter } from "next/navigation";
import { SearchCategoryFilters } from "@/components/catalog/search-category-filters";
import { Button } from "@/components/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { useShopCatalog } from "@/contexts/shop-catalog-provider";
import { useShopDisplaySettings } from "@/contexts/shop-display-settings-provider";
import { useShopBrowseUrlSync } from "@/hooks/shop/use-shop-browse-url-sync";
import { useShopScrollRestore } from "@/hooks/shop/use-shop-scroll-restore";
import { trackShopViewItem } from "@/lib/gtm-shop-events";
import { DEFAULT_SHOP_SORT, SHOP_STOREFRONT_GRID_CLASS_NAME, getShopCatalogWidthClass, getShopGroupItemsLayoutClasses, opensShopProductPage, shopOtherSectionId, shouldGroupShopItems } from "@/lib/shop-catalog-view";
import { type ShopSection, getShopItemPagePath, isEmbeddedShopPath } from "@/lib/shop-links";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { GiftCardShopCartLine, RegularCatalogShopItem, ShopCartLine, ShopCategory, ShopItem, isCustomAmountGiftCard } from "@/types/shop";
import { isRegularCatalogShopItem, lineFromGiftCard, lineFromRegularItem } from "./cart-lines";
import CatalogItemDetailsDialog from "./item-details-dialog";
interface MixedCatalogSectionProps {
  initialSection?: ShopSection | null;
  lines?: ShopCartLine[];
  onAddLine: (line: ShopCartLine) => void;
  onUpdateQuantity?: (lineId: string, quantity: number) => void;
}
export interface CatalogGroup {
  id: string;
  title: string;
  description?: string | null;
  items: ShopItem[];
}
const gridClasses = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3";
const OTHER_CATEGORY = "Other";

/**
 * Uncategorized items get one section per item type instead of a single mixed
 * bucket. Gift cards drop the "Other" prefix — they are rarely categorized, so
 * "Other" would be the only place a shopper ever sees them. Listed in the order
 * the sections render in.
 */
const OTHER_GROUP_TITLES: [ItemType, string][] = [[ItemType.SERVICE, "Other Services"], [ItemType.PACKAGE, "Other Packages"], [ItemType.PRODUCT, "Other Products"], [ItemType.MEDICATION, "Other Medications"], [ItemType.GIFT_CARD, "Gift Cards"]];
const otherGroupTitleByType = new Map(OTHER_GROUP_TITLES);
const otherGroupOrderByType = new Map(OTHER_GROUP_TITLES.map(([type], index) => [type, index]));
const itemOrder = (item: ShopItem): number => typeof item.sortOrder === "number" ? item.sortOrder : Number.POSITIVE_INFINITY;
const compareItems = (a: ShopItem, b: ShopItem, inputOrderById: Map<string, number>): number => {
  // The catalog API returns items in their storefront rank order. Keep that
  // order authoritative so optimistic drag updates render exactly as stored;
  // inventory sortOrder is only a fallback for callers without input order.
  const inputOrderDiff = (inputOrderById.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (inputOrderById.get(b.id) ?? Number.MAX_SAFE_INTEGER);
  if (inputOrderDiff !== 0) return inputOrderDiff;
  const sortDiff = itemOrder(a) - itemOrder(b);
  if (sortDiff !== 0) return sortDiff;
  return a.name.localeCompare(b.name, undefined, {
    sensitivity: "base"
  });
};
export const itemHasPromotionId = (item: ShopItem, promotionId: string): boolean => [item.bestPromotion, ...(item.applicablePromotions ?? []), ...(item.availablePromotions ?? [])].some(promotion => promotion?.id === promotionId);
export const orderPromotedCatalogItems = (items: ShopItem[], promotionId?: string | null): ShopItem[] => {
  const inputOrderById = new Map(items.map((item, index) => [item.id, index]));
  return items.filter(item => hasPromotion(item) && (!promotionId || itemHasPromotionId(item, promotionId))).sort((a, b) => compareItems(a, b, inputOrderById));
};
const compareCategoryNames = (a: string, b: string): number => {
  const aOther = a.trim().toLowerCase() === OTHER_CATEGORY.toLowerCase();
  const bOther = b.trim().toLowerCase() === OTHER_CATEGORY.toLowerCase();
  if (aOther && !bOther) return 1;
  if (!aOther && bOther) return -1;
  return a.localeCompare(b, undefined, {
    sensitivity: "base"
  });
};
const buildTopLevelCategoryLookup = (categories: ShopCategory[]): Map<string, string> => {
  const categoryByName = new Map<string, ShopCategory>();
  for (const category of categories) {
    if (!category.isOther) categoryByName.set(category.name, category);
  }
  const roots = new Map<string, string>();
  const resolveRoot = (name: string, seen = new Set<string>()): string => {
    const cached = roots.get(name);
    if (cached) return cached;
    const category = categoryByName.get(name);
    const parentName = category?.parentCategoryName?.trim() || null;
    if (!category || !parentName || !categoryByName.has(parentName) || seen.has(name)) {
      roots.set(name, name);
      return name;
    }
    seen.add(name);
    const root = resolveRoot(parentName, seen);
    roots.set(name, root);
    return root;
  };
  for (const category of categories) {
    if (!category.isOther) resolveRoot(category.name);
  }
  return roots;
};
export const findSectionTargetGroupId = (groups: CatalogGroup[], section: ShopSection | null | undefined): string | null => {
  const itemType = section === "gift-cards" ? ItemType.GIFT_CARD : section === "packages" ? ItemType.PACKAGE : null;
  if (!itemType) return null;
  return groups.find(group => group.items.some(item => item.type === itemType))?.id ?? null;
};

/**
 * Split the uncategorized bucket into one section per item type. Types without
 * a title of their own stay together under a single "Other" section.
 */
const buildUncategorizedGroups = (itemsInBucket: ShopItem[], sortItems: (items: ShopItem[]) => ShopItem[]): CatalogGroup[] => {
  const groups = new Map<string, {
    title: string;
    order: number;
    items: ShopItem[];
  }>();
  for (const item of itemsInBucket) {
    const title = otherGroupTitleByType.get(item.type);
    const id = title ? shopOtherSectionId(item.type) : OTHER_CATEGORY;
    const group = groups.get(id);
    if (group) {
      group.items.push(item);
      continue;
    }
    groups.set(id, {
      title: title ?? OTHER_CATEGORY,
      order: otherGroupOrderByType.get(item.type) ?? OTHER_GROUP_TITLES.length,
      items: [item]
    });
  }
  return Array.from(groups.entries()).sort(([, a], [, b]) => a.order - b.order).map(([id, group]) => ({
    id,
    title: group.title,
    description: null,
    items: sortItems(group.items)
  }));
};
export const groupCatalogItems = (items: ShopItem[], categories: ShopCategory[]): CatalogGroup[] => {
  const topLevelCategoryByName = buildTopLevelCategoryLookup(categories);
  const inputOrderById = new Map(items.map((item, index) => [item.id, index]));
  const bucketItems = (itemsToSort: ShopItem[]): ShopItem[] => [...itemsToSort].sort((a, b) => compareItems(a, b, inputOrderById));
  const buckets = new Map<string, ShopItem[]>();
  for (const item of items) {
    const itemCategory = item.category?.trim() || OTHER_CATEGORY;
    const key = itemCategory === OTHER_CATEGORY ? OTHER_CATEGORY : topLevelCategoryByName.get(itemCategory) ?? itemCategory;
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(key, [item]);
    }
  }
  const result: CatalogGroup[] = [];
  const placedBuckets = new Set<string>();
  const orderedCategories = [...categories].sort((a, b) => a.order - b.order);
  for (const category of orderedCategories) {
    const categoryName = category.isOther ? OTHER_CATEGORY : category.name;
    const bucketKey = category.isOther ? OTHER_CATEGORY : topLevelCategoryByName.get(categoryName) ?? categoryName;
    if (bucketKey !== categoryName) continue;
    const itemsInBucket = buckets.get(bucketKey) ?? [];
    if (itemsInBucket.length === 0 && !category.isSynthetic) continue;
    placedBuckets.add(bucketKey);
    if (category.isOther) {
      result.push(...buildUncategorizedGroups(itemsInBucket, bucketItems));
      continue;
    }
    result.push({
      id: bucketKey,
      title: category.name,
      description: category.description ?? null,
      items: bucketItems(itemsInBucket)
    });
  }
  for (const [name, itemsInBucket] of Array.from(buckets.entries()).sort((a, b) => compareCategoryNames(a[0], b[0]))) {
    if (placedBuckets.has(name)) continue;
    if (name === OTHER_CATEGORY) {
      result.push(...buildUncategorizedGroups(itemsInBucket, bucketItems));
      continue;
    }
    result.push({
      id: name,
      title: name,
      description: null,
      items: bucketItems(itemsInBucket)
    });
  }
  return result;
};
export const CatalogItemTile: React.FC<{
  item: ShopItem;
  onSelect?: () => void;
  compact?: boolean;
  opensDialog?: boolean;
  placement?: ShopCatalogItemPlacement;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
}> = t0 => {
  const $ = _c(44);
  const {
    item,
    onSelect,
    compact: t1,
    opensDialog,
    placement,
    quantity,
    onQuantityChange
  } = t0;
  const compact = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== onQuantityChange || $[1] !== quantity) {
    t2 = {
      quantity,
      onQuantityChange
    };
    $[0] = onQuantityChange;
    $[1] = quantity;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const quantityProps = t2;
  switch (item.type) {
    case ItemType.GIFT_CARD:
      {
        let t3;
        if ($[3] !== compact || $[4] !== item || $[5] !== onSelect || $[6] !== quantityProps) {
          t3 = <GiftCardShopTile item={item} onSelect={onSelect} compact={compact} {...quantityProps} />;
          $[3] = compact;
          $[4] = item;
          $[5] = onSelect;
          $[6] = quantityProps;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        return t3;
      }
    case ItemType.PACKAGE:
      {
        let t3;
        if ($[8] !== item || $[9] !== onSelect || $[10] !== quantityProps) {
          t3 = <PackageTile item={item} onSelect={onSelect} {...quantityProps} />;
          $[8] = item;
          $[9] = onSelect;
          $[10] = quantityProps;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        return t3;
      }
    case ItemType.MEMBERSHIP:
      {
        let t3;
        if ($[12] !== compact || $[13] !== item || $[14] !== onSelect || $[15] !== opensDialog || $[16] !== placement) {
          t3 = <MembershipTile item={item} onSelect={onSelect} compact={compact} opensDialog={opensDialog} placement={placement} />;
          $[12] = compact;
          $[13] = item;
          $[14] = onSelect;
          $[15] = opensDialog;
          $[16] = placement;
          $[17] = t3;
        } else {
          t3 = $[17];
        }
        return t3;
      }
    case ItemType.SERVICE:
      {
        let t3;
        if ($[18] !== compact || $[19] !== item || $[20] !== onSelect || $[21] !== opensDialog || $[22] !== placement || $[23] !== quantityProps) {
          t3 = <ServiceTile item={item} onSelect={onSelect} compact={compact} opensDialog={opensDialog} placement={placement} {...quantityProps} />;
          $[18] = compact;
          $[19] = item;
          $[20] = onSelect;
          $[21] = opensDialog;
          $[22] = placement;
          $[23] = quantityProps;
          $[24] = t3;
        } else {
          t3 = $[24];
        }
        return t3;
      }
    case ItemType.PRODUCT:
      {
        let t3;
        if ($[25] !== compact || $[26] !== item || $[27] !== onSelect || $[28] !== opensDialog || $[29] !== placement || $[30] !== quantityProps) {
          t3 = <ProductTile item={item} onSelect={onSelect} compact={compact} opensDialog={opensDialog} placement={placement} {...quantityProps} />;
          $[25] = compact;
          $[26] = item;
          $[27] = onSelect;
          $[28] = opensDialog;
          $[29] = placement;
          $[30] = quantityProps;
          $[31] = t3;
        } else {
          t3 = $[31];
        }
        return t3;
      }
    case ItemType.MEDICATION:
      {
        let t3;
        if ($[32] !== compact || $[33] !== item || $[34] !== onSelect || $[35] !== opensDialog || $[36] !== placement || $[37] !== quantityProps) {
          t3 = <MedicationTile item={item} onSelect={onSelect} compact={compact} opensDialog={opensDialog} placement={placement} {...quantityProps} />;
          $[32] = compact;
          $[33] = item;
          $[34] = onSelect;
          $[35] = opensDialog;
          $[36] = placement;
          $[37] = quantityProps;
          $[38] = t3;
        } else {
          t3 = $[38];
        }
        return t3;
      }
    default:
      {
        let t3;
        if ($[39] !== compact || $[40] !== item || $[41] !== onSelect || $[42] !== quantityProps) {
          t3 = <ProductTile item={item} onSelect={onSelect} compact={compact} {...quantityProps} />;
          $[39] = compact;
          $[40] = item;
          $[41] = onSelect;
          $[42] = quantityProps;
          $[43] = t3;
        } else {
          t3 = $[43];
        }
        return t3;
      }
  }
};
const PROMOTION_SCROLL_PX_PER_SECOND = 36;
const NO_GIFT_ITEMS: ShopItem[] = [];
export type ShopCatalogItemPlacement = "promotion" | "category";
export type ShopCatalogItemRenderer = (item: ShopItem, options: {
  compact: boolean;
  placement: ShopCatalogItemPlacement;
  groupId?: string;
}) => React.ReactNode;
export type ShopCatalogPromotionItemsRenderer = (options: {
  items: ShopItem[];
  renderItem: ShopCatalogItemRenderer;
}) => React.ReactNode;
export const PromotionCarousel: React.FC<{
  items: ShopItem[];
  giftItems?: ShopItem[];
  renderItem: ShopCatalogItemRenderer;
  renderItems?: ShopCatalogPromotionItemsRenderer;
  focusedPromotionName?: string | null;
  onClearPromotionFocus?: () => void;
}> = t0 => {
  const $ = _c(31);
  const {
    items: catalogItems,
    giftItems: t1,
    renderItem: renderCatalogItem,
    renderItems,
    focusedPromotionName,
    onClearPromotionFocus
  } = t0;
  const giftItems = t1 === undefined ? NO_GIFT_ITEMS : t1;
  let t2;
  if ($[0] !== catalogItems || $[1] !== giftItems) {
    t2 = [...giftItems, ...catalogItems];
    $[0] = catalogItems;
    $[1] = giftItems;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const items = t2;
  let t3;
  if ($[3] !== giftItems || $[4] !== renderCatalogItem) {
    t3 = (item, options) => giftItems.includes(item) ? <CatalogItemTile item={item} compact={options.compact} placement={options.placement} /> : renderCatalogItem(item, options);
    $[3] = giftItems;
    $[4] = renderCatalogItem;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const renderItem = t3;
  const scrollerRef = useRef(null);
  const {
    displayStyle
  } = useShopDisplaySettings();
  const widthClass = getShopCatalogWidthClass(displayStyle);
  let t4;
  if ($[6] !== items.length) {
    t4 = shouldGroupShopItems(items.length);
    $[6] = items.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const useCarousel = t4;
  const [autoScroll, setAutoScroll] = useState(useCarousel);
  let t5;
  if ($[8] !== useCarousel) {
    t5 = () => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollLeft = 0;
      }
      setAutoScroll(useCarousel);
    };
    $[8] = useCarousel;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== items || $[11] !== useCarousel) {
    t6 = [items, useCarousel];
    $[10] = items;
    $[11] = useCarousel;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== items.length) {
    t7 = () => {
      setAutoScroll(wasAutoScrolling => {
        if (!wasAutoScrolling) {
          return false;
        }
        const scroller = scrollerRef.current;
        if (scroller && items.length >= 2) {
          const loopWidth = scroller.scrollWidth / 2;
          if (loopWidth > 0) {
            scroller.scrollLeft = scroller.scrollLeft % loopWidth;
          }
        }
        return false;
      });
    };
    $[13] = items.length;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const stopAutoScroll = t7;
  let t8;
  if ($[15] !== autoScroll || $[16] !== stopAutoScroll || $[17] !== useCarousel) {
    t8 = () => {
      if (!useCarousel || !autoScroll) {
        return;
      }
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (prefersReducedMotion) {
        setAutoScroll(false);
        return;
      }
      const scroller_0 = scrollerRef.current;
      if (!scroller_0) {
        return;
      }
      let frameId = 0;
      let lastTimestamp = null;
      let expectedScrollLeft = scroller_0.scrollLeft;
      const onScroll = () => {
        if (Math.abs(scroller_0.scrollLeft - expectedScrollLeft) < 1) {
          return;
        }
        stopAutoScroll();
      };
      const tick = timestamp => {
        if (lastTimestamp === null) {
          lastTimestamp = timestamp;
        }
        const deltaSeconds = (timestamp - lastTimestamp) / 1000;
        lastTimestamp = timestamp;
        const loopWidth_0 = scroller_0.scrollWidth / 2;
        if (loopWidth_0 > 0) {
          expectedScrollLeft = expectedScrollLeft + PROMOTION_SCROLL_PX_PER_SECOND * deltaSeconds;
          expectedScrollLeft;
          if (expectedScrollLeft >= loopWidth_0) {
            expectedScrollLeft = expectedScrollLeft - loopWidth_0;
            expectedScrollLeft;
          }
          scroller_0.scrollLeft = expectedScrollLeft;
          expectedScrollLeft = scroller_0.scrollLeft;
        }
        frameId = requestAnimationFrame(tick);
      };
      scroller_0.addEventListener("scroll", onScroll, {
        passive: true
      });
      frameId = requestAnimationFrame(tick);
      return () => {
        cancelAnimationFrame(frameId);
        scroller_0.removeEventListener("scroll", onScroll);
      };
    };
    $[15] = autoScroll;
    $[16] = stopAutoScroll;
    $[17] = useCarousel;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== autoScroll || $[20] !== items || $[21] !== stopAutoScroll || $[22] !== useCarousel) {
    t9 = [autoScroll, items, stopAutoScroll, useCarousel];
    $[19] = autoScroll;
    $[20] = items;
    $[21] = stopAutoScroll;
    $[22] = useCarousel;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  useEffect(t8, t9);
  if (items.length === 0 && !focusedPromotionName) {
    return null;
  }
  const loopItems = autoScroll && items.length >= 2 ? [...items, ...items] : items;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground"><FiTag className="h-4 w-4" /></span>;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h2 className="text-foreground font-serif text-h1 font-normal">Promotions</h2>;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = focusedPromotionName ? `Showing items for ${focusedPromotionName}.` : "Featured offers are also listed in their categories below.";
  let t13;
  if ($[26] !== t12) {
    t13 = <div className="flex-1">{t11}<p className="text-caption text-foreground-secondary">{t12}</p></div>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== focusedPromotionName || $[29] !== onClearPromotionFocus) {
    t14 = focusedPromotionName && onClearPromotionFocus && <button type="button" className="text-caption-emphasis text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" onClick={onClearPromotionFocus}>Show all promotions</button>;
    $[28] = focusedPromotionName;
    $[29] = onClearPromotionFocus;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return <section className="relative mx-auto mb-10 w-full max-w-7xl"><div className={cx("mx-auto mb-4 flex items-center gap-2", widthClass)}>{t10}{t13}{t14}</div>{items.length === 0 ? <div className={cx("mx-auto rounded-lg border border-border bg-surface p-6 text-center", widthClass)}><p className="text-body text-foreground-secondary">No catalog items are currently available for this promotion.</p>{onClearPromotionFocus && <button type="button" className="mt-3 text-body-emphasis text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" onClick={onClearPromotionFocus}>Show all promotions</button>}</div> : renderItems ? <div className={cx(!useCarousel && cx("mx-auto w-full", widthClass))}>{renderItems({
        items,
        renderItem
      })}</div> : !useCarousel ? <div className={cx("mx-auto w-full", widthClass, getShopGroupItemsLayoutClasses(items, items.length, displayStyle))}>{items.map(item_0 => <div key={item_0.id}>{renderItem(item_0, {
          compact: false,
          placement: "promotion"
        })}</div>)}</div> : <div className="relative -mx-4"><div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 h-full hidden md:block w-16 bg-gradient-to-r from-background to-transparent" /><div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 h-full hidden md:block w-16 bg-gradient-to-l from-background to-transparent" /><div ref={scrollerRef} className={cx("flex gap-4 overflow-x-auto px-12 pb-2", !autoScroll && "snap-x")} onClick={stopAutoScroll} onFocusCapture={stopAutoScroll}>{loopItems.map((item_1, index) => {
          const isClone = index >= items.length;
          return <div key={isClone ? `${item_1.id}-clone` : item_1.id} aria-hidden={isClone || undefined} inert={isClone || undefined} className={cx("w-[18rem] flex-none", !autoScroll && "snap-start")}>{renderItem(item_1, {
              compact: true,
              placement: "promotion"
            })}</div>;
        })}</div></div>}</section>;
};
export type ShopCatalogGroupRenderer = (options: {
  group: CatalogGroup;
  renderItem: ShopCatalogItemRenderer;
}) => React.ReactNode;
export type ShopCatalogGroupsRenderer = (options: {
  groups: CatalogGroup[];
  renderGroup: (group: CatalogGroup) => React.ReactNode;
}) => React.ReactNode;
export interface ShopCatalogContentProps {
  catalog: {
    items: ShopItem[];
    categories: ShopCategory[];
  };
  /** Full, unfiltered shop count, when catalog.items is a filtered page. */
  catalogItemCount?: number;
  initialSection?: ShopSection | null;
  onSelectItem?: (item: ShopItem) => void;
  renderItem?: ShopCatalogItemRenderer;
  renderPromotionItems?: ShopCatalogPromotionItemsRenderer;
  renderGroupItems?: ShopCatalogGroupRenderer;
  renderGroups?: ShopCatalogGroupsRenderer;
  /** Display-only free-gift tiles shown first in Promotions. */
  giftItems?: ShopItem[];
  selectedPromotionId?: string | null;
  selectedPromotionName?: string | null;
  onClearPromotionFocus?: () => void;
}
function CatalogGroups(t0) {
  const $ = _c(6);
  const {
    groups,
    renderGroup,
    renderGroups
  } = t0;
  let t1;
  if ($[0] !== groups || $[1] !== renderGroup || $[2] !== renderGroups) {
    t1 = renderGroups ? renderGroups({
      groups,
      renderGroup
    }) : groups.map(renderGroup);
    $[0] = groups;
    $[1] = renderGroup;
    $[2] = renderGroups;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <>{t1}</>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * Shared mixed-catalog presentation used by both the live shop and its
 * settings preview. Purchase state and settings controls stay outside this
 * component so the catalog cannot accidentally couple the two surfaces.
 */
export function ShopCatalogContent({
  catalog,
  catalogItemCount = catalog.items.length,
  initialSection,
  onSelectItem,
  renderItem: renderItemProp,
  renderPromotionItems,
  renderGroupItems,
  renderGroups,
  giftItems,
  selectedPromotionId,
  selectedPromotionName,
  onClearPromotionFocus
}: ShopCatalogContentProps) {
  const {
    displayStyle
  } = useShopDisplaySettings();
  const widthClass = getShopCatalogWidthClass(displayStyle);
  const shouldGroupItems = shouldGroupShopItems(catalogItemCount);
  const promotedItems = useMemo(() => orderPromotedCatalogItems(catalog.items, selectedPromotionId), [catalog.items, selectedPromotionId]);
  const groups = useMemo(() => {
    const catalogGroups = shouldGroupItems ? groupCatalogItems(catalog.items, catalog.categories) : [{
      id: "shop-items",
      title: "",
      items: catalog.items
    }];
    if (catalogGroups.length !== 1 || promotedItems.length === 0 || promotedItems.length < catalog.items.length - promotedItems.length) {
      return catalogGroups;
    }

    // Only remove items shown in Promotions so focusing an offer cannot make
    // items from other promotions disappear from the shop entirely.
    const promotedIds = new Set(promotedItems.map(item => item.id));
    const group = catalogGroups[0];
    const items = group.items.filter(item_0 => !promotedIds.has(item_0.id));
    return items.length > 0 ? [{
      ...group,
      items
    }] : [];
  }, [catalog.categories, catalog.items, promotedItems, shouldGroupItems]);
  const initialSectionGroupId = findSectionTargetGroupId(groups, initialSection);
  const initialSectionElementId = initialSection ? `shop-section-${initialSection}` : null;
  useEffect(() => {
    if (!initialSectionElementId) return;
    const frameId = requestAnimationFrame(() => {
      document.getElementById(initialSectionElementId)?.scrollIntoView({
        block: "start"
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [initialSectionElementId]);
  const renderItem = useCallback<ShopCatalogItemRenderer>((item_1, options) => renderItemProp ? renderItemProp(item_1, options) : <CatalogItemTile item={item_1} compact={options.compact} opensDialog={Boolean(onSelectItem)} placement={options.placement} onSelect={onSelectItem ? () => onSelectItem(item_1) : undefined} />, [onSelectItem, renderItemProp]);
  const defaultGroupItems: ShopCatalogGroupRenderer = ({
    group: group_0
  }) => <div className={getShopGroupItemsLayoutClasses(group_0.items, catalogItemCount, displayStyle)}>
      {group_0.items.map(item_2 => <div key={item_2.id}>
          {renderItem(item_2, {
        compact: false,
        placement: "category",
        groupId: group_0.id
      })}
        </div>)}
    </div>;
  const renderGroup = (group_1: CatalogGroup) => {
    const renderGroupContent = renderGroupItems ?? defaultGroupItems;
    if (!shouldGroupItems) {
      return <div key={group_1.id} id={initialSectionElementId ?? undefined}>
          {group_1.items.length > 0 && renderGroupContent({
          group: group_1,
          renderItem
        })}
        </div>;
    }
    return <section key={group_1.id} data-shop-category={group_1.id} id={group_1.id === initialSectionGroupId ? initialSectionElementId ?? undefined : undefined}>
        <div className="mb-4">
          <h2 className="text-foreground font-serif text-h1 font-normal">
            {group_1.title}
          </h2>
          {group_1.description && <p className="mt-1 max-w-2xl text-body text-foreground-secondary">
              {group_1.description}
            </p>}
        </div>
        {group_1.items.length > 0 && renderGroupContent({
        group: group_1,
        renderItem
      })}
      </section>;
  };
  return <>
      <PromotionCarousel items={promotedItems} giftItems={giftItems} renderItem={renderItem} renderItems={renderPromotionItems} focusedPromotionName={selectedPromotionName} onClearPromotionFocus={onClearPromotionFocus} />

      <div className={cx("mx-auto w-full space-y-12", widthClass)}>
        <CatalogGroups groups={groups} renderGroup={renderGroup} renderGroups={shouldGroupItems ? renderGroups : undefined} />
      </div>
    </>;
}
export const MixedCatalogSection: React.FC<MixedCatalogSectionProps> = t0 => {
  const $ = _c(120);
  const {
    initialSection,
    lines: t1,
    onAddLine,
    onUpdateQuantity
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const lines = t2;
  const {
    catalog,
    isLoading,
    isError,
    promotionFocus,
    view
  } = useShopCatalog();
  let t3;
  if ($[2] !== view?.state) {
    t3 = view?.state ?? {
      searchQuery: "",
      selectedCategory: undefined,
      sort: DEFAULT_SHOP_SORT
    };
    $[2] = view?.state;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const filterState = t3;
  let t4;
  if ($[4] !== view?.actions) {
    t4 = view?.actions ?? {
      setSearchQuery: _temp,
      setSelectedCategory: _temp2,
      setSort: _temp3,
      clearFilters: _temp4
    };
    $[4] = view?.actions;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const filterActions = t4;
  let t5;
  if ($[6] !== catalog.items || $[7] !== view?.derived) {
    t5 = view?.derived ?? {
      filteredItems: catalog.items,
      categories: [],
      resultCount: catalog.items.length,
      hasActiveFilters: false,
      emptyStateReason: null,
      showSearch: false,
      showCategories: false,
      showSort: false
    };
    $[6] = catalog.items;
    $[7] = view?.derived;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const filterDerived = t5;
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const {
    displayStyle
  } = useShopDisplaySettings();
  let t6;
  if ($[9] !== displayStyle) {
    t6 = item => opensShopProductPage(item, displayStyle);
    $[9] = displayStyle;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const opensProductPage = t6;
  const t7 = Boolean(view) && !isLoading;
  let t8;
  if ($[11] !== filterState.selectedCategory || $[12] !== filterState.sort || $[13] !== t7) {
    t8 = {
      enabled: t7,
      category: filterState.selectedCategory,
      sort: filterState.sort
    };
    $[11] = filterState.selectedCategory;
    $[12] = filterState.sort;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useShopBrowseUrlSync(t8);
  const sectionRef = useRef(null);
  const rememberScroll = useShopScrollRestore(sectionRef, !isLoading && !isError && catalog.items.length > 0);
  let t9;
  if ($[15] !== catalog || $[16] !== filterDerived.filteredItems) {
    t9 = {
      ...catalog,
      items: filterDerived.filteredItems
    };
    $[15] = catalog;
    $[16] = filterDerived.filteredItems;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const filteredCatalog = t9;
  const focusedPromotionId = promotionFocus?.state.focusedPromotionId;
  let t10;
  if ($[18] !== catalog.promotions || $[19] !== filterDerived.hasActiveFilters || $[20] !== focusedPromotionId) {
    t10 = filterDerived.hasActiveFilters ? [] : giftPromotionItems(catalog.promotions.filter(promotion => !focusedPromotionId || promotion.id === focusedPromotionId));
    $[18] = catalog.promotions;
    $[19] = filterDerived.hasActiveFilters;
    $[20] = focusedPromotionId;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const giftItems = t10;
  const [giftCardSelection, setGiftCardSelection] = useState(null);
  const [packageSelection, setPackageSelection] = useState(null);
  const [membershipSelection, setMembershipSelection] = useState(null);
  const [catalogItemSelection, setCatalogItemSelection] = useState(null);
  const catalogItemTriggerRef = useRef(null);
  let result;
  if ($[22] !== lines) {
    result = new Map();
    for (const line of lines) {
      if ("itemId" in line) {
        result.set(`${line.itemType}:${line.itemId}`, line);
      }
    }
    $[22] = lines;
    $[23] = result;
  } else {
    result = $[23];
  }
  const lineByCatalogItemKey = result;
  let t11;
  if ($[24] !== filterState || $[25] !== opensProductPage || $[26] !== params || $[27] !== pathname || $[28] !== rememberScroll || $[29] !== router) {
    t11 = item_0 => {
      if (opensProductPage(item_0) && params?.tenant) {
        rememberScroll(item_0.id);
        router.push(getShopItemPagePath(params.tenant, item_0.id, {
          category: filterState.selectedCategory,
          sort: filterState.sort === DEFAULT_SHOP_SORT ? null : filterState.sort
        }, isEmbeddedShopPath(pathname)), {
          transitionTypes: [shopPhotoTransitionType(item_0.id)]
        });
        return;
      }
      trackShopViewItem(item_0);
      if (item_0.type === ItemType.GIFT_CARD) {
        setGiftCardSelection(isCustomAmountGiftCard(item_0) ? {
          kind: "custom",
          item: item_0
        } : {
          kind: "item",
          item: item_0
        });
        return;
      }
      if (item_0.type === ItemType.PACKAGE) {
        setPackageSelection(item_0);
        return;
      }
      if (item_0.type === ItemType.MEMBERSHIP) {
        setMembershipSelection(item_0);
        return;
      }
      if (isRegularCatalogShopItem(item_0)) {
        catalogItemTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setCatalogItemSelection(item_0);
      }
    };
    $[24] = filterState;
    $[25] = opensProductPage;
    $[26] = params;
    $[27] = pathname;
    $[28] = rememberScroll;
    $[29] = router;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleSelectItem = t11;
  let t12;
  if ($[31] !== onAddLine) {
    t12 = line_0 => {
      onAddLine(line_0);
      setGiftCardSelection(null);
    };
    $[31] = onAddLine;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const handleConfirmGiftCard = t12;
  let t13;
  if ($[33] !== onAddLine) {
    t13 = item_1 => {
      onAddLine(packageLineFromItem(item_1));
      setPackageSelection(null);
    };
    $[33] = onAddLine;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const handleConfirmPackage = t13;
  let t14;
  if ($[35] !== onAddLine) {
    t14 = line_1 => {
      onAddLine(line_1);
      setMembershipSelection(null);
    };
    $[35] = onAddLine;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const handleConfirmMembership = t14;
  let t15;
  if ($[37] !== onAddLine) {
    t15 = item_2 => {
      onAddLine(lineFromRegularItem(item_2));
      setCatalogItemSelection(null);
    };
    $[37] = onAddLine;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handleConfirmCatalogItem = t15;
  let t16;
  if ($[39] !== catalog.items || $[40] !== lineByCatalogItemKey || $[41] !== onAddLine || $[42] !== onUpdateQuantity) {
    t16 = (itemId, quantity) => {
      const promotionItem = catalog.items.find(item_3 => item_3.id === itemId);
      if (!promotionItem || promotionItem.alreadyMember) {
        return;
      }
      const existingLine = lineByCatalogItemKey.get(`${promotionItem.type}:${promotionItem.id}`);
      if (existingLine && onUpdateQuantity) {
        onUpdateQuantity(existingLine.lineId, existingLine.quantity + quantity);
        return;
      }
      if (promotionItem.type === ItemType.GIFT_CARD && isCustomAmountGiftCard(promotionItem)) {
        setPackageSelection(null);
        setCatalogItemSelection(null);
        setGiftCardSelection({
          kind: "custom",
          item: promotionItem
        });
      } else {
        if (promotionItem.type === ItemType.GIFT_CARD) {
          onAddLine(withLineQuantity(lineFromGiftCard(promotionItem), quantity));
        } else {
          if (promotionItem.type === ItemType.PACKAGE) {
            onAddLine({
              ...packageLineFromItem(promotionItem),
              quantity
            });
          } else {
            if (promotionItem.type === ItemType.MEMBERSHIP) {
              setGiftCardSelection(null);
              setPackageSelection(null);
              setCatalogItemSelection(null);
              setMembershipSelection(promotionItem);
            } else {
              if (isRegularCatalogShopItem(promotionItem)) {
                onAddLine({
                  ...lineFromRegularItem(promotionItem),
                  quantity
                });
              }
            }
          }
        }
      }
    };
    $[39] = catalog.items;
    $[40] = lineByCatalogItemKey;
    $[41] = onAddLine;
    $[42] = onUpdateQuantity;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  const handleQuickAddPromotionItem = t16;
  if (isLoading && displayStyle === "STOREFRONT") {
    let t17;
    if ($[44] !== displayStyle) {
      t17 = cx("mx-auto w-full", getShopCatalogWidthClass(displayStyle));
      $[44] = displayStyle;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    let t18;
    let t19;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Skeleton className="mb-6 h-14 w-full rounded-lg" />;
      t19 = <Skeleton className="mb-4 h-8 w-56" />;
      $[46] = t18;
      $[47] = t19;
    } else {
      t18 = $[46];
      t19 = $[47];
    }
    let t20;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <div className={SHOP_STOREFRONT_GRID_CLASS_NAME}>{[0, 1, 2, 3, 4, 5, 6, 7].map(_temp5)}</div>;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] !== t17) {
      t21 = <section className={t17} aria-busy="true" aria-label="Loading the shop">{t18}{t19}{t20}</section>;
      $[49] = t17;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    return t21;
  }
  if (isLoading) {
    let t17;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <header className="mb-6"><p className="text-overline text-primary">Shop</p><h1 className="mt-2 text-h1 text-foreground">Browse the clinic catalog</h1><p className="mt-2 max-w-2xl text-body text-foreground-secondary">Loading available services, products, packages, and gift cards.</p></header>;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    let t18;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <section className="mx-auto w-full max-w-5xl">{t17}<div className={gridClasses}>{[0, 1, 2, 3, 4, 5].map(_temp6)}</div></section>;
      $[52] = t18;
    } else {
      t18 = $[52];
    }
    return t18;
  }
  if (isError) {
    let t17;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <section className="rounded-2xl border border-status-error-text/20 bg-status-error-bg p-5 text-status-error-text">We couldn't load this clinic's shop catalog. Please try again in a moment.</section>;
      $[53] = t17;
    } else {
      t17 = $[53];
    }
    return t17;
  }
  if (catalog.items.length === 0) {
    let t17;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="py-16 text-center"><p className="text-foreground-secondary">No items are currently available.</p></div>;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    return t17;
  }
  let t17;
  if ($[55] !== displayStyle) {
    t17 = cx("mx-auto mb-6 flex w-full flex-col gap-3 sm:flex-row sm:items-end", getShopCatalogWidthClass(displayStyle));
    $[55] = displayStyle;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== filterActions.setSearchQuery || $[58] !== filterActions.setSelectedCategory || $[59] !== filterDerived.categories || $[60] !== filterDerived.showCategories || $[61] !== filterDerived.showSearch || $[62] !== filterState.searchQuery || $[63] !== filterState.selectedCategory) {
    t18 = <SearchCategoryFilters className="min-w-0 flex-1" searchLabel="Search shop" searchPlaceholder="Search by item name or description..." searchValue={filterState.searchQuery} onSearchChange={filterActions.setSearchQuery} showSearch={filterDerived.showSearch} categoryLabel="Choose from a list of categories" categoryValue={filterState.selectedCategory} onCategoryChange={filterActions.setSelectedCategory} categories={filterDerived.categories} showCategories={filterDerived.showCategories} />;
    $[57] = filterActions.setSearchQuery;
    $[58] = filterActions.setSelectedCategory;
    $[59] = filterDerived.categories;
    $[60] = filterDerived.showCategories;
    $[61] = filterDerived.showSearch;
    $[62] = filterState.searchQuery;
    $[63] = filterState.selectedCategory;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== filterActions.setSort || $[66] !== filterDerived.showSort || $[67] !== filterState.sort) {
    t19 = filterDerived.showSort && <ShopSortSelect className="sm:ml-auto" value={filterState.sort} onChange={filterActions.setSort} />;
    $[65] = filterActions.setSort;
    $[66] = filterDerived.showSort;
    $[67] = filterState.sort;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] !== t17 || $[70] !== t18 || $[71] !== t19) {
    t20 = <div className={t17}>{t18}{t19}</div>;
    $[69] = t17;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== catalog.items.length || $[74] !== catalog.promotions || $[75] !== displayStyle || $[76] !== filterActions.clearFilters || $[77] !== filterDerived.emptyStateReason || $[78] !== filteredCatalog || $[79] !== focusedPromotionId || $[80] !== giftItems || $[81] !== handleSelectItem || $[82] !== initialSection || $[83] !== lineByCatalogItemKey || $[84] !== onUpdateQuantity || $[85] !== opensProductPage || $[86] !== promotionFocus?.actions) {
    t21 = filterDerived.emptyStateReason === "filters" ? <div className={cx("mx-auto w-full rounded-2xl border border-border p-8 text-center", getShopCatalogWidthClass(displayStyle))}><h2 className="text-h2 text-foreground">No matching items</h2><p className="mt-2 text-body text-foreground-secondary">Try another search or category.</p><Button className="mt-4" variant="secondary" onClick={filterActions.clearFilters}>Clear filters</Button></div> : <ShopCatalogContent catalog={filteredCatalog} catalogItemCount={catalog.items.length} initialSection={initialSection} giftItems={giftItems} selectedPromotionId={focusedPromotionId} selectedPromotionName={catalog.promotions.find(promotion_0 => promotion_0.id === focusedPromotionId)?.name} onClearPromotionFocus={promotionFocus?.actions.clearPromotionFocus} onSelectItem={handleSelectItem} renderItem={(item_4, options) => {
      const line_2 = lineByCatalogItemKey.get(`${item_4.type}:${item_4.id}`);
      return <CatalogItemTile item={item_4} compact={options.compact} opensDialog={!opensProductPage(item_4)} placement={options.placement} onSelect={() => handleSelectItem(item_4)} quantity={line_2?.quantity} onQuantityChange={line_2 && onUpdateQuantity ? quantity_0 => onUpdateQuantity(line_2.lineId, quantity_0) : undefined} />;
    }} />;
    $[73] = catalog.items.length;
    $[74] = catalog.promotions;
    $[75] = displayStyle;
    $[76] = filterActions.clearFilters;
    $[77] = filterDerived.emptyStateReason;
    $[78] = filteredCatalog;
    $[79] = focusedPromotionId;
    $[80] = giftItems;
    $[81] = handleSelectItem;
    $[82] = initialSection;
    $[83] = lineByCatalogItemKey;
    $[84] = onUpdateQuantity;
    $[85] = opensProductPage;
    $[86] = promotionFocus?.actions;
    $[87] = t21;
  } else {
    t21 = $[87];
  }
  let t22;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setGiftCardSelection(null);
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  let t23;
  if ($[89] !== catalog.items || $[90] !== giftCardSelection || $[91] !== handleConfirmGiftCard || $[92] !== handleQuickAddPromotionItem || $[93] !== lines) {
    t23 = <GiftCardConfiguratorDialog selection={giftCardSelection} catalogItems={catalog.items} cartLines={lines} onConfirm={handleConfirmGiftCard} onQuickAddPromotionItem={handleQuickAddPromotionItem} onCancel={t22} />;
    $[89] = catalog.items;
    $[90] = giftCardSelection;
    $[91] = handleConfirmGiftCard;
    $[92] = handleQuickAddPromotionItem;
    $[93] = lines;
    $[94] = t23;
  } else {
    t23 = $[94];
  }
  let t24;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setPackageSelection(null);
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] !== catalog.items || $[97] !== handleConfirmPackage || $[98] !== handleQuickAddPromotionItem || $[99] !== lines || $[100] !== packageSelection) {
    t25 = <PackageDetailsDialog item={packageSelection} catalogItems={catalog.items} cartLines={lines} onConfirm={handleConfirmPackage} onQuickAddPromotionItem={handleQuickAddPromotionItem} onCancel={t24} />;
    $[96] = catalog.items;
    $[97] = handleConfirmPackage;
    $[98] = handleQuickAddPromotionItem;
    $[99] = lines;
    $[100] = packageSelection;
    $[101] = t25;
  } else {
    t25 = $[101];
  }
  let t26;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setMembershipSelection(null);
    $[102] = t26;
  } else {
    t26 = $[102];
  }
  let t27;
  if ($[103] !== handleConfirmMembership || $[104] !== membershipSelection) {
    t27 = <MembershipConfiguratorDialog item={membershipSelection} onConfirm={handleConfirmMembership} onCancel={t26} />;
    $[103] = handleConfirmMembership;
    $[104] = membershipSelection;
    $[105] = t27;
  } else {
    t27 = $[105];
  }
  let t28;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setCatalogItemSelection(null);
    $[106] = t28;
  } else {
    t28 = $[106];
  }
  let t29;
  if ($[107] !== catalog.items || $[108] !== catalogItemSelection || $[109] !== handleConfirmCatalogItem || $[110] !== handleQuickAddPromotionItem || $[111] !== lines) {
    t29 = <CatalogItemDetailsDialog item={catalogItemSelection} catalogItems={catalog.items} cartLines={lines} onConfirm={handleConfirmCatalogItem} onQuickAddPromotionItem={handleQuickAddPromotionItem} onCancel={t28} returnFocusRef={catalogItemTriggerRef} />;
    $[107] = catalog.items;
    $[108] = catalogItemSelection;
    $[109] = handleConfirmCatalogItem;
    $[110] = handleQuickAddPromotionItem;
    $[111] = lines;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  let t30;
  if ($[113] !== t20 || $[114] !== t21 || $[115] !== t23 || $[116] !== t25 || $[117] !== t27 || $[118] !== t29) {
    t30 = <section ref={sectionRef}>{t20}{t21}{t23}{t25}{t27}{t29}</section>;
    $[113] = t20;
    $[114] = t21;
    $[115] = t23;
    $[116] = t25;
    $[117] = t27;
    $[118] = t29;
    $[119] = t30;
  } else {
    t30 = $[119];
  }
  return t30;
};
export default MixedCatalogSection;
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {}
function _temp5(n) {
  return <StorefrontProductTileSkeleton key={n} />;
}
function _temp6(n_0) {
  return <CatalogTileSkeleton key={n_0} />;
}